/**
 * Shared navigation config for the LOGGED-IN app shell.
 *
 * Two surfaces consume this:
 *  - PRIMARY_ITEMS    → the 3 quick pills shown in the sticky header next to the
 *                       logo (Community Feed, Knowledge Hub, Campaigns).
 *  - SIDEBAR_SECTIONS → everything else, rendered in the LEFT sidebar (desktop)
 *                       and inside the mobile slide-in drawer: Secondary
 *                       destinations, Administration [admins only], Account.
 *
 * Labels are i18n keys under the `nav` namespace (resolved via
 * `t('nav.<key>')`), rendering in EN/HI/MR. Only non-text structure
 * (key + path + icon + grouping) lives here.
 *
 * Distinct from `src/components/portal/nav.ts`, which is the PUBLIC portal nav.
 */
import {
  Home, BookOpen, Users, Megaphone, Trophy, MessageCircle, Bell, Bookmark, Video,
  LayoutDashboard, FileText, FolderOpen, Newspaper, Bot, Award, Quote, MapPin,
  HandHeart, Mail, BarChart3, User, Settings,
  ScrollText, Activity, type LucideIcon,
} from 'lucide-react'

export interface AppNavItem {
  /** i18n key under the `nav` namespace (label resolved via `t('nav.<key>')`). */
  key: string
  /** fallback English label (used if translation missing / for grep-ability). */
  label: string
  path: string
  icon: LucideIcon
  /** `true` for items whose path is dynamic and needs the signed-in user's username. */
  needsUsername?: boolean
}

export interface AppNavSection {
  /** i18n key under `nav` for the section heading. Omit for an unlabeled section. */
  titleKey?: string
  items: AppNavItem[]
  /** only render this section for ADMIN / SUPER_ADMIN */
  adminOnly?: boolean
  /** hide this section from ADMIN / SUPER_ADMIN (used to hide the user-facing
   *  Secondary destinations from admins so their sidebar shows only the clean
   *  Administration section — regular users still see it). */
  hideForAdmin?: boolean
  /** only render this section for VOLUNTEER_COMMUNITY users (volunteer-unique
   *  destinations that live in the separate /community-dashboard shell). */
  volunteerOnly?: boolean
  /** pin to the bottom of the container (visual separation) */
  bottom?: boolean
}

// ──────────────────────────────────────────────────────────────────────────
// PRIMARY — the 3 header pills (near search), shown to every signed-in user
// ──────────────────────────────────────────────────────────────────────────
export const PRIMARY_ITEMS: AppNavItem[] = [
  { key: 'feed', label: 'Community Feed', path: '/feed', icon: Home },
  { key: 'knowledge', label: 'Knowledge Hub', path: '/knowledge', icon: BookOpen },
  { key: 'campaigns', label: 'Campaigns', path: '/campaigns', icon: Megaphone },
]

// ──────────────────────────────────────────────────────────────────────────
// SECONDARY — inside the "More" dropdown
// ──────────────────────────────────────────────────────────────────────────
export const SECONDARY_ITEMS: AppNavItem[] = [
  { key: 'groups', label: 'Groups', path: '/groups', icon: Users },
  { key: 'competitions', label: 'Competitions', path: '/competitions', icon: Trophy },
  { key: 'badges', label: 'Badges', path: '/badges', icon: Award },
  { key: 'certificates', label: 'Certificates', path: '/certificates', icon: ScrollText },
  { key: 'messages', label: 'Messages', path: '/messages', icon: MessageCircle },
  { key: 'notifications', label: 'Notifications', path: '/notifications', icon: Bell },
  { key: 'savedPosts', label: 'Saved', path: '/saved', icon: Bookmark },
  { key: 'reels', label: 'Reels', path: '/reels', icon: Video },
]

// ──────────────────────────────────────────────────────────────────────────
// ADMINISTRATION — back-office tools, admins only, inside "More".
// Ordered management-first (the 10 core management surfaces), then the
// remaining back-office tools. All existing tools are retained — nothing
// is removed (regression-safe).
// ──────────────────────────────────────────────────────────────────────────
const ADMIN_ITEMS: AppNavItem[] = [
  { key: 'dashboard', label: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
  { key: 'activeUsers', label: 'Active Users', path: '/admin/active-users', icon: Activity },
  { key: 'users', label: 'User Management', path: '/admin/users', icon: Users },
  { key: 'groupManagement', label: 'Group Management', path: '/admin/groups-admin', icon: Users },
  { key: 'postManager', label: 'Post Manager', path: '/admin/posts', icon: FileText },
  { key: 'reelManagement', label: 'Reel Management', path: '/admin/reels-admin', icon: Video },
  { key: 'badges', label: 'Badge Management', path: '/admin/badges', icon: Award },
  { key: 'certificates', label: 'Certificate Management', path: '/admin/certificates', icon: ScrollText },
  { key: 'newsAdmin', label: 'News Manager', path: '/admin/news', icon: Newspaper },
  { key: 'competitions', label: 'Competition Management', path: '/admin/competitions', icon: Trophy },
  // — remaining back-office tools —
  { key: 'content', label: 'Content Moderation', path: '/admin/content', icon: FileText },
  { key: 'resources', label: 'Resources', path: '/admin/resources', icon: FolderOpen },
  { key: 'videosAdmin', label: 'Videos', path: '/admin/videos', icon: Video },
  { key: 'assistantAdmin', label: 'TB Assistant', path: '/admin/assistant', icon: Bot },
  { key: 'testimonials', label: 'Testimonials', path: '/admin/testimonials', icon: Quote },
  { key: 'facilities', label: 'Facilities', path: '/admin/facilities', icon: MapPin },
  { key: 'supporters', label: 'Supporters', path: '/admin/supporters', icon: HandHeart },
  { key: 'oathManagement', label: 'Oath Management', path: '/admin/oaths', icon: ScrollText },
  { key: 'contactInbox', label: 'Contact Inbox', path: '/admin/contact', icon: Mail },
  { key: 'reports', label: 'Reports', path: '/admin/reports', icon: BarChart3 },
]

// ──────────────────────────────────────────────────────────────────────────
// ACCOUNT — inside "More", pinned to the bottom
// ──────────────────────────────────────────────────────────────────────────
const ACCOUNT_ITEMS: AppNavItem[] = [
  { key: 'profile', label: 'Profile', path: '/profile', icon: User, needsUsername: true },
  { key: 'settings', label: 'Settings', path: '/settings', icon: Settings },
]

// ──────────────────────────────────────────────────────────────────────────
// VOLUNTEER COMMUNITY — no dedicated sidebar section. VOLUNTEER_COMMUNITY
// users land on /feed (see utils/userTypeDestination.ts) and use the same
// sidebar as everyone else. The community workspace (Dashboard, Volunteer
// Opportunities, My Activities, Learning, Community, Resources) remains
// reachable by navigating to /community-dashboard/* directly.
// ──────────────────────────────────────────────────────────────────────────

/**
 * LEFT sidebar sections (desktop) / mobile drawer: Secondary destinations
 * (unlabeled), Administration (admins only), Account (pinned to bottom).
 * These are the items that used to live in the "More ▾" dropdown.
 */
export const SIDEBAR_SECTIONS: AppNavSection[] = [
  { items: SECONDARY_ITEMS, hideForAdmin: true },
  { titleKey: 'nav.adminSection', adminOnly: true, items: ADMIN_ITEMS },
  { titleKey: 'nav.accountSection', bottom: true, items: ACCOUNT_ITEMS },
]

/**
 * Resolve an item's path, substituting the signed-in user's username when
 * `needsUsername` is set (Profile link).
 */
export function resolvePath(item: AppNavItem, username?: string | null): string {
  if (item.needsUsername) return `/profile/${username ?? ''}`
  return item.path
}